import {
  CheckoutAppSelector,
  useAppSelector,
  RecoverableTerminalErrorsState,
  setRecoverableFlowStatus,
  useAppDispatch,
  setCardsAndBankAccounts,
  useInitiateInstallmentPaymentMutation,
  setRecoverableFlowTraceId,
  usePreferredCard,
  selectRecoverableDeclinesPaymentMethod,
  setCardForOverdueACH,
  setPreferredPaymentMethod,
  useApplePayFeature,
  useApplePayState,
  useAppRouter,
  usePreferredBankAccount,
  usePreferredPaymentMethod,
  useSelectedPaymentMethod,
  useShouldSetApplePayAsPurchaseMethod,
  ApplePayState,
  useCheckoutState,
  useCohort,
  setCashAppPayDetails,
  setCashAppPay,
} from '..'
import { CardBrand, CardFlowType, isCashAppPay, isSavedCard, OverdueNotification, PaymentMethodType, RecoverableTerminalErrorStatus } from '@afterpay/types'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Text } from '@afterpaytouch/core'
import { Cohort, ErrorCode, isFetchBaseQueryError, OrderResponse, OverduePaymentResponse } from '@afterpaytouch/portal-api'
import { getAllScheduleIds } from './helper'
import {
  ApplePayError,
  checkoutErrors,
  Features,
  InitiateMutationResponse,
  RecoverableTerminalErrorsNotification,
  TerminalErrorCode,
  RecoverableErrorCode,
} from '../../model'
import { AppLink } from '../../component'
import { Route } from '../../config/router'
import { useCheckoutSizes } from '../../utils/responsive'
import { createSelector, SerializedError } from '@reduxjs/toolkit'
import { useNonInitialEffect } from '../../utils/hooks'
import { setRecoverableFlowPageReady } from './reducer'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { Trans, useTranslation } from '../../utils/convergence'
import { useFlag } from '@afterpaytouch/feature'
import { isUndefinedOrEmpty } from '@afterpay/utils'

/* eslint-disable @afterpay/i18n-no-dynamic-context */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getRecoverableHeading = (t: any, reason: string, context: string) => {
  switch (reason) {
    case 'invalidBrand':
      return t('recoverableTerminalError:error.invalidBrand.heading', { context })
    case 'cardExpiry':
      return t('recoverableTerminalError:error.cardExpiry.heading', { context })
    case 'achNotAllowed':
      return t('recoverableTerminalError:error.achNotAllowed.heading', { context })
    case 'creditCardNotAllowed':
      return t('recoverableTerminalError:error.creditCardNotAllowed.heading', { context })
    case 'cardExpiryLimitInMonths':
      return t('recoverableTerminalError:error.cardExpiryLimitInMonths.heading', { context })
    default:
      return ''
  }
}
/* eslint-enable @afterpay/i18n-no-dynamic-context */

/* eslint-disable @afterpay/i18n-require-context */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSummaryHeading = (t: any, key: string) => {
  switch (key) {
    // Backend error keys (not part of TerminalErrorCode enum)
    case 'dss_payment_method_prohibited':
      return t('summary:error.dss_payment_method_prohibited.heading', { defaultValue: '' })
    case 'error_with_missing_heading_copy':
      return t('summary:error.error_with_missing_heading_copy.heading', { defaultValue: '' })

    case TerminalErrorCode.AmountTooHigh:
      return t('summary:error.amountTooHigh.heading', { defaultValue: '' })
    case TerminalErrorCode.ChargebackPending:
      return t('summary:error.chargebackPending.heading', { defaultValue: '' })
    case TerminalErrorCode.OrderAmountExceedsSpendCap:
      return t('summary:error.orderAmountExceedsSpendCap.heading', { defaultValue: '' })
    case TerminalErrorCode.OrderAmountTooHigh:
      return t('summary:error.orderAmountTooHigh.heading', { defaultValue: '' })
    case TerminalErrorCode.AccountClosed:
      return t('summary:error.accountClosed.heading', { defaultValue: '' })
    case TerminalErrorCode.AccountDisabled:
      return t('summary:error.accountDisabled.heading', { defaultValue: '' })
    case TerminalErrorCode.AccountFrozen:
      return t('summary:error.accountFrozen.heading', { defaultValue: '' })
    case TerminalErrorCode.AccountPendingDeletion:
      return t('summary:error.accountPendingDeletion.heading', { defaultValue: '' })
    case TerminalErrorCode.OverduePayments:
      return t('summary:error.overdue.heading', { defaultValue: '' })
    case TerminalErrorCode.OverduePaymentsCheckoutPaymentEligible:
      return t('summary:error.overduePaymentEligible.heading', { defaultValue: '' })
    case TerminalErrorCode.IneligibleState:
      return t('summary:error.ineligibleState.heading', { defaultValue: '' })
    case TerminalErrorCode.MinimumNotMet:
      return t('summary:error.minimumNotMet.heading', { defaultValue: '' })
    case TerminalErrorCode.RiskDeclined:
      return t('summary:error.riskDeclined.heading', { defaultValue: '' })
    case TerminalErrorCode.InvalidPreorder:
      return t('summary:error.invalidPreorder.heading', { defaultValue: '' })
    case TerminalErrorCode.UnsupportedOrder:
      return t('summary:error.unsupportedOrder.heading', { defaultValue: '' })
    case TerminalErrorCode.ExcessiveLateFeeAmount:
      return t('summary:error.excessiveLateFeeAmount.heading', { defaultValue: '' })
    case TerminalErrorCode.ExcessiveLateFeeNumber:
      return t('summary:error.excessiveLateFeeNumber.heading', { defaultValue: '' })
    case TerminalErrorCode.MerchantOrderAmountLimitExceeded:
      return t('summary:error.merchantOrderAmountLimitExceeded.heading', { defaultValue: '' })
    case TerminalErrorCode.MaxActiveOrdersReached:
      return t('summary:error.maxActiveOrdersReached.heading', { defaultValue: '' })
    case TerminalErrorCode.UnsupportedMerchantTradingCountry:
      return t('summary:error.unsupportedMerchantTradingCountry.heading', { defaultValue: '' })
    case TerminalErrorCode.InvalidPickupAddress:
      return t('summary:error.invalidPickupAddress.heading', { defaultValue: '' })
    case TerminalErrorCode.UnableToUpdateCheckout:
      return t('summary:error.unableToUpdateCheckout.heading', { defaultValue: '' })
    case TerminalErrorCode.InvalidAmount:
      return t('summary:error.invalidAmount.heading', { defaultValue: '' })
    case TerminalErrorCode.GiftCardIneligible:
      return t('summary:error.giftCardIneligible.heading', { defaultValue: '' })
    case TerminalErrorCode.MerchantLimitZero:
      return t('summary:error.merchantLimitZero.heading', { defaultValue: '' })
    case TerminalErrorCode.IdentityDeclined:
      return t('summary:error.identityDeclined.heading', { defaultValue: '' })
    case TerminalErrorCode.StateRestrictionsPCL:
      return t('summary:error.stateRestrictionsPCL.heading', { defaultValue: '' })
    case TerminalErrorCode.IdentityVerificationDeclinedPCL:
      return t('summary:error.identityVerificationDeclinedPCL.heading', { defaultValue: '' })
    case TerminalErrorCode.PaywatchCheckFailed:
      return t('summary:error.paywatchCheckFailed.heading', { defaultValue: '' })
    case TerminalErrorCode.ShadowAccountIneligible:
      return t('summary:error.shadowAccountIneligible.heading', { defaultValue: '' })
    case TerminalErrorCode.DuplicatePaymentCardError:
      return t('summary:error.duplicatePaymentCardError.heading', { defaultValue: '' })
    case TerminalErrorCode.ExceededMaxAttemptsEnteringDetails:
      return t('summary:error.exceededMaxAttemptsEnteringDetails.heading', { defaultValue: '' })
    case TerminalErrorCode.ComplianceDeclineBureauNoHit:
      return t('summary:error.complianceDeclineBureauNoHit.heading', { defaultValue: '' })
    case TerminalErrorCode.ComplianceDeclineBureau:
      return t('summary:error.complianceDeclineBureau.heading', { defaultValue: '' })
    case TerminalErrorCode.CreditCheckFailure:
      return t('summary:error.creditCheckFailure.heading', { defaultValue: '' })
    case TerminalErrorCode.LimitInternalError:
      return t('summary:error.limitInternalError.heading', { defaultValue: '' })
    case TerminalErrorCode.UnsuitabilityDecline:
      return t('summary:error.unsuitabilityDecline.heading', { defaultValue: '' })
    case TerminalErrorCode.KYCRefreshPending:
      return t('summary:error.KYCRefreshPending.heading', { defaultValue: '' })
    case RecoverableErrorCode.UnsupportedCardIssuerBank:
      return t('summary:error.unsupportedCardIssuerBank.heading', { defaultValue: '' })
    case RecoverableErrorCode.UnsupportedCardIssuer:
      return t('summary:error.foreignCard.heading', { defaultValue: '' })
    case RecoverableErrorCode.UnsupportedCardType:
      return t('summary:error.prepaidCard.heading', { defaultValue: '' })
    case RecoverableErrorCode.AddPaymentActiveCardLimitExceeded:
      return t('summary:error.activeCardLimitExceeded.heading', { defaultValue: '' })
    case RecoverableErrorCode.AddPaymentInactiveCardLimitExceeded:
      return t('summary:error.inactiveCardLimitExceeded.heading', { defaultValue: '' })
    case RecoverableErrorCode.CreditCardProhibitedForPaymentType:
      return t('summary:error.creditCardProhibitedForPaymentType.heading', { defaultValue: '' })
    case RecoverableErrorCode.CashAppCardIneligible:
      return t('summary:error.cashAppCardIneligible.heading', { defaultValue: '' })
    case 'applePay':
      return t('summary:error.applePay.heading', { defaultValue: '' })
    default:
      return t('summary:error.default.heading', { defaultValue: '' })
  }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSummaryMessage = (t: any, key: string) => {
  switch (key) {
    // Backend error keys (not part of TerminalErrorCode enum)
    case 'dss_payment_method_prohibited':
      return t('summary:error.dss_payment_method_prohibited.message')
    case 'error_with_missing_heading_copy':
      return t('summary:error.error_with_missing_heading_copy.message')

    case TerminalErrorCode.AmountTooHigh:
      return t('summary:error.amountTooHigh.message')
    case TerminalErrorCode.ChargebackPending:
      return t('summary:error.chargebackPending.message')
    case TerminalErrorCode.OrderAmountExceedsSpendCap:
      return t('summary:error.orderAmountExceedsSpendCap.message')
    case TerminalErrorCode.OrderAmountTooHigh:
      return t('summary:error.orderAmountTooHigh.message')
    case TerminalErrorCode.AccountClosed:
      return t('summary:error.accountClosed.message')
    case TerminalErrorCode.AccountDisabled:
      return t('summary:error.accountDisabled.message')
    case TerminalErrorCode.AccountFrozen:
      return t('summary:error.accountFrozen.message')
    case TerminalErrorCode.AccountPendingDeletion:
      return t('summary:error.accountPendingDeletion.message')
    case TerminalErrorCode.OverduePayments:
      return t('summary:error.overdue.message')
    case TerminalErrorCode.OverduePaymentsCheckoutPaymentEligible:
      return t('summary:error.overduePaymentEligible.message')
    case TerminalErrorCode.IneligibleState:
      return t('summary:error.ineligibleState.message')
    case TerminalErrorCode.MinimumNotMet:
      return t('summary:error.minimumNotMet.message')
    case TerminalErrorCode.RiskDeclined:
      return t('summary:error.riskDeclined.message')
    case TerminalErrorCode.InvalidPreorder:
      return t('summary:error.invalidPreorder.message')
    case TerminalErrorCode.UnsupportedOrder:
      return t('summary:error.unsupportedOrder.message')
    case TerminalErrorCode.ExcessiveLateFeeAmount:
      return t('summary:error.excessiveLateFeeAmount.message')
    case TerminalErrorCode.ExcessiveLateFeeNumber:
      return t('summary:error.excessiveLateFeeNumber.message')
    case TerminalErrorCode.MerchantOrderAmountLimitExceeded:
      return t('summary:error.merchantOrderAmountLimitExceeded.message')
    case TerminalErrorCode.MaxActiveOrdersReached:
      return t('summary:error.maxActiveOrdersReached.message')
    case TerminalErrorCode.UnsupportedMerchantTradingCountry:
      return t('summary:error.unsupportedMerchantTradingCountry.message')
    case TerminalErrorCode.InvalidPickupAddress:
      return t('summary:error.invalidPickupAddress.message')
    case TerminalErrorCode.UnableToUpdateCheckout:
      return t('summary:error.unableToUpdateCheckout.message')
    case TerminalErrorCode.InvalidAmount:
      return t('summary:error.invalidAmount.message')
    case TerminalErrorCode.GiftCardIneligible:
      return t('summary:error.giftCardIneligible.message')
    case TerminalErrorCode.MerchantLimitZero:
      return t('summary:error.merchantLimitZero.message')
    case TerminalErrorCode.IdentityDeclined:
      return t('summary:error.identityDeclined.message')
    case TerminalErrorCode.StateRestrictionsPCL:
      return t('summary:error.stateRestrictionsPCL.message')
    case TerminalErrorCode.IdentityVerificationDeclinedPCL:
      return t('summary:error.identityVerificationDeclinedPCL.message')
    case TerminalErrorCode.PaywatchCheckFailed:
      return t('summary:error.paywatchCheckFailed.message')
    case TerminalErrorCode.ShadowAccountIneligible:
      return t('summary:error.shadowAccountIneligible.message')
    case TerminalErrorCode.DuplicatePaymentCardError:
      return t('summary:error.duplicatePaymentCardError.message')
    case TerminalErrorCode.ExceededMaxAttemptsEnteringDetails:
      return t('summary:error.exceededMaxAttemptsEnteringDetails.message')
    case TerminalErrorCode.ComplianceDeclineBureauNoHit:
      return t('summary:error.complianceDeclineBureauNoHit.message')
    case TerminalErrorCode.ComplianceDeclineBureau:
      return t('summary:error.complianceDeclineBureau.message')
    case TerminalErrorCode.CreditCheckFailure:
      return t('summary:error.creditCheckFailure.message')
    case TerminalErrorCode.LimitInternalError:
      return t('summary:error.limitInternalError.message')
    case TerminalErrorCode.UnsuitabilityDecline:
      return t('summary:error.unsuitabilityDecline.message')
    case TerminalErrorCode.KYCRefreshPending:
      return t('summary:error.KYCRefreshPending.message')
    case RecoverableErrorCode.UnsupportedCardIssuerBank:
      return t('summary:error.unsupportedCardIssuerBank.message')
    case RecoverableErrorCode.UnsupportedCardIssuer:
      return t('summary:error.foreignCard.message')
    case RecoverableErrorCode.UnsupportedCardType:
      return t('summary:error.prepaidCard.message')
    case RecoverableErrorCode.AddPaymentActiveCardLimitExceeded:
      return t('summary:error.activeCardLimitExceeded.message')
    case RecoverableErrorCode.AddPaymentInactiveCardLimitExceeded:
      return t('summary:error.inactiveCardLimitExceeded.message')
    case RecoverableErrorCode.CreditCardProhibitedForPaymentType:
      return t('summary:error.creditCardProhibitedForPaymentType.message')
    case RecoverableErrorCode.CashAppCardIneligible:
      return t('summary:error.cashAppCardIneligible.message')
    case 'applePay':
      return t('summary:error.applePay.message')
    default:
      return t('summary:error.default.message')
  }
}

/* eslint-enable @afterpay/i18n-require-context */

/* Selectors */
export const recoverableTerminalErrorsStateSelector: CheckoutAppSelector<RecoverableTerminalErrorsState> = (state) => state.recoverableTerminalErrors
export const recoverableFlowTraceIdSelector: CheckoutAppSelector<string> = createSelector(
  recoverableTerminalErrorsStateSelector,
  (state) => state?.traceId ?? ''
)

/* Helper for non hooks */
export const isRecoverableFlow = (status: RecoverableTerminalErrorStatus): boolean =>
  status > RecoverableTerminalErrorStatus.NotStarted && status < RecoverableTerminalErrorStatus.Finished

/* React Hooks */
export const useIsRecoverableFlow = (): boolean => {
  const { status } = useAppSelector(recoverableTerminalErrorsStateSelector)
  return isRecoverableFlow(status)
}

export const useIsOverduePaymentsFlow = (): boolean => {
  const { error } = useCheckoutState()
  const isRecoverableFlow = useIsRecoverableFlow()

  return error?.errorCode === ErrorCode.OverduePaymentsCheckoutPaymentEligible && isRecoverableFlow
}

export const useIsRecoverableFlowPageReady = (): boolean => {
  const { isPageReady } = useAppSelector(recoverableTerminalErrorsStateSelector)
  return isPageReady
}

export const useRecoverableTerminalErrorsState = (): RecoverableTerminalErrorsState => useAppSelector(recoverableTerminalErrorsStateSelector)

// @ts-expect-error: OPERATION BLEED STOPPER
export const useRecoverableFlowSelectedOrder = (): OrderResponse | OverduePaymentResponse => useRecoverableTerminalErrorsState().selectedOrder

export const useHandleInitiateMutation = (isQuerySuccess: boolean): InitiateMutationResponse => {
  const [initiateInstallmentPayments, { data, isLoading: isInitiateLoading, isSuccess: isInitiateSuccess, isError }] = useInitiateInstallmentPaymentMutation()
  const selectedOrder = useRecoverableFlowSelectedOrder()

  useNonInitialEffect(() => {
    if (typeof selectedOrder === 'undefined') {
      return
    }

    if (isQuerySuccess) {
      initiateInstallmentPayments({
        paymentScheduleIds: getAllScheduleIds(selectedOrder),
        flow: CardFlowType.RECOVERY_ONFILE,
      })
    }
  }, [selectedOrder, isQuerySuccess])

  return {
    // @ts-expect-error: OPERATION BLEED STOPPER
    data,
    isInitiateLoading,
    isInitiateSuccess,
    isError,
  }
}

export const useHandlePaymentMethodsSetup = (initiatePayload: InitiateMutationResponse, enabledCardTypes: CardBrand[] = []): void => {
  const { data, isInitiateLoading: isLoading, isInitiateSuccess } = initiatePayload
  const { status } = useRecoverableTerminalErrorsState()
  const dispatch = useAppDispatch()
  const cohort = useCohort()
  const { savedBankAccounts, savedCards, traceId, cashAppPay } = data ?? {}

  useEffect(() => {
    if (isInitiateSuccess && !isLoading && status === RecoverableTerminalErrorStatus.InitiatedInstallmentPayment) {
      // set up preferred cards and bank accounts in store for topaz
      dispatch(
        setCardsAndBankAccounts({
          savedCards,
          savedBankAccounts,
          enabledCardTypes,
        })
      )

      // set up cash app pay if user relies on cash app pay and data is valid
      const cashAppPayDetails = cashAppPay?.[0]
      if (cohort === Cohort.CashOnly && isCashAppPay(cashAppPayDetails)) {
        dispatch(setCashAppPayDetails(cashAppPayDetails))
        const validCashAppPay = cashAppPay?.filter((cashAppPay) => isCashAppPay(cashAppPay))
        dispatch(setCashAppPay(validCashAppPay))
      }

      dispatch(setRecoverableFlowTraceId(traceId))
      dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.HandlePurchaseMethods))
    }
  }, [isInitiateSuccess, isLoading, status, dispatch, enabledCardTypes, savedBankAccounts, savedCards, traceId])
}

export const useRecoverableNotificationMsg = (): RecoverableTerminalErrorsNotification => {
  const { switched, reason, valid } = usePreferredCard() ?? {}
  const isRecoverableFlow = useIsRecoverableFlow()
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { t } = useTranslation()

  const notificationMsg = useMemo(() => {
    const messageOptions = switched ? 'switched' : 'cannotSwitch'

    if (reason === null || !isRecoverableFlow) {
      return null
    }

    return {
      heading: (
        <Text size={textSize} renderAs='div' bold>
          {getRecoverableHeading(t, reason || '', messageOptions)}
        </Text>
      ),
      message: (
        <Text size={smallTextSize} renderAs='div'>
          {(() => {
            const components = {
              msgLink: <AppLink underline noMargin inline route={Route.ADD_PAYMENT_METHOD} />,
            }
            switch (reason) {
              case 'invalidBrand':
                return <Trans i18nKey='recoverableTerminalError:error.invalidBrand.message' components={components} context={messageOptions} />
              case 'cardExpiry':
                return <Trans i18nKey='recoverableTerminalError:error.cardExpiry.message' components={components} context={messageOptions} />
              case 'achNotAllowed':
                return <Trans i18nKey='recoverableTerminalError:error.achNotAllowed.message' components={components} context={messageOptions} />
              case 'creditCardNotAllowed':
                return <Trans i18nKey='recoverableTerminalError:error.creditCardNotAllowed.message' components={components} context={messageOptions} />
              case 'cardExpiryLimitInMonths':
                return <Trans i18nKey='recoverableTerminalError:error.cardExpiryLimitInMonths.message' components={components} context={messageOptions} />
              default:
                return null
            }
          })()}
        </Text>
      ),
      severe: !valid,
    }
  }, [reason, switched, t, textSize, smallTextSize, isRecoverableFlow, valid])

  // @ts-expect-error: OPERATION BLEED STOPPER
  return notificationMsg
}

export const useHandlePurchaseMethods = (setNotification: Dispatch<SetStateAction<OverdueNotification>>): void => {
  const dispatch = useAppDispatch()
  const { status } = useRecoverableTerminalErrorsState()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const { account } = usePreferredBankAccount() ?? {}
  const isApplePayEnabled = useApplePayFeature()
  const { isEnabledForDevice } = useApplePayState()
  const router = useAppRouter()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly

  const { card, switched, reason, valid } = usePreferredCard() ?? {}
  const notificationMsg = useRecoverableNotificationMsg()

  useEffect(() => {
    const handlePurchaseMethods = async (): Promise<void> => {
      try {
        // mimics Summary page's doStartCheckout
        if (shouldUseApplePay) {
          await dispatch(selectRecoverableDeclinesPaymentMethod({ type: PaymentMethodType.APPLE_PAY }))
          dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
          dispatch(setRecoverableFlowPageReady(true))
        } else if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && card !== null) {
          // if ACH is the preferred payment method, automatically switch to card.
          // If ACH is set up, show ACH as a payment method but disabled
          dispatch(
            setCardForOverdueACH({
              // @ts-expect-error: OPERATION BLEED STOPPER
              card,
              switched: true,
              reason: 'achNotAllowed',
              // @ts-expect-error: OPERATION BLEED STOPPER
              valid: card.error === null,
            })
          )

          dispatch(setPreferredPaymentMethod(PaymentMethodType.CREDIT_CARD))
        } else if (card !== null) {
          await dispatch(
            selectRecoverableDeclinesPaymentMethod(
              {
                // @ts-expect-error: OPERATION BLEED STOPPER
                card: { token: card.token },
                type: PaymentMethodType.CREDIT_CARD,
              },
              false
            )
          )

          if (reason !== null) {
            setNotification(notificationMsg)
          }
          dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
          dispatch(setRecoverableFlowPageReady(true))
        } else {
          if (isCashOnlyCohort) {
            dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
            dispatch(setRecoverableFlowPageReady(true))
          } else {
            dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.LackOfEligiblePaymentMethod))
            router.push(Route.ADD_PAYMENT_METHOD)
          }
        }
      } catch (e) {
        dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Error))
        if (e instanceof Error) {
          capture.error(e)
        }
        router.push(Route.TERMINAL_ERROR)
      }
    }

    if (status === RecoverableTerminalErrorStatus.HandlePurchaseMethods) {
      handlePurchaseMethods()
    }

    // Removed 'router' from dep array as it was causing rerenders
  }, [
    card,
    dispatch,
    shouldUseApplePay,
    status,
    isApplePayEnabled.new,
    isEnabledForDevice,
    preferredPaymentMethod,
    reason,
    switched,
    valid,
    account,
    notificationMsg,
  ])
}

export const useHandleRecoverableSetup = (): RecoverableTerminalErrorsNotification | null => {
  const { card, reason, valid } = usePreferredCard() ?? {}
  const { textSize } = useCheckoutSizes()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  // @ts-expect-error: OPERATION BLEED STOPPER
  const [notification, setNotification] = useState<RecoverableTerminalErrorsNotification>(null)
  const notificationMsg = useRecoverableNotificationMsg()

  useEffect(() => {
    // To persist the AMEX warning when there are no valid cards
    // and the payment method hasn't changed
    if (
      !valid &&
      selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD &&
      'card' in selectedPaymentMethod &&
      // @ts-expect-error: OPERATION BLEED STOPPER
      isSavedCard(selectedPaymentMethod?.card) &&
      // @ts-expect-error: OPERATION BLEED STOPPER
      selectedPaymentMethod.card.token === card.token &&
      reason !== null
    ) {
      setNotification(notificationMsg)
    }
  }, [card, valid, reason, selectedPaymentMethod, textSize])

  useHandlePurchaseMethods(setNotification)

  return notification
}

export const useIsRecoverableFlowReadyToConfirm = (): boolean => {
  const { status } = useRecoverableTerminalErrorsState()

  return status === RecoverableTerminalErrorStatus.Started
}

export const useRecoverableFlowTraceId = (): string => useAppSelector(recoverableFlowTraceIdSelector)

export const useSetErrorKeys = (
  confirmPayError: FetchBaseQueryError | SerializedError | undefined,
  setErrorKey: React.Dispatch<React.SetStateAction<string>>
): void => {
  useEffect(() => {
    let currentErrorKey
    if (isFetchBaseQueryError(confirmPayError)) {
      currentErrorKey = checkoutErrors[confirmPayError?.data?.errorCode ?? '']
    }
    setErrorKey(currentErrorKey)
  }, [confirmPayError, setErrorKey])
}

interface SetErrorFieldParams {
  heading?: string
  message: string
  severe?: boolean
}

export const useSetErrorFields = (
  isConfirmPayError: boolean,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  setClicked: React.Dispatch<React.SetStateAction<boolean>>,
  setNotification: React.Dispatch<React.SetStateAction<RecoverableTerminalErrorsNotification | null>>,
  errorKey: string | undefined,
  applepay: ApplePayState
): void => {
  const { t } = useTranslation()

  useEffect(() => {
    const setErrorFields = ({ heading, message, severe = false }: SetErrorFieldParams): void => {
      setClicked(false)
      if (!isUndefinedOrEmpty(heading)) {
        setNotification({ heading, message, severe })
        setErrorMessage('')
      } else {
        setNotification(null)
        setErrorMessage(message)
      }
    }

    const getErrorKeyFromApplePayError = (): string => {
      switch (applepay.error) {
        case ApplePayError.InvalidCardType:
          return 'prepaidCard'
        default:
          return 'applePay'
      }
    }

    const hasErrorKey = typeof errorKey !== 'undefined'
    const isMappedError = isConfirmPayError && hasErrorKey
    const isApplepayError = typeof applepay.error !== 'undefined'
    const isUnmappedConfirmPayError = isConfirmPayError && !hasErrorKey

    const currentErrorKey = isMappedError ? errorKey : getErrorKeyFromApplePayError()
    const heading = getSummaryHeading(t, currentErrorKey)
    const message = getSummaryMessage(t, currentErrorKey)

    if (isMappedError) {
      setErrorFields({
        ...(!isUndefinedOrEmpty(heading) && { heading }),
        message,
        severe: false,
      })
    } else if (isApplepayError) {
      setErrorFields({
        ...(!isUndefinedOrEmpty(heading) && { heading }),
        message,
        severe: true,
      })
    } else if (isUnmappedConfirmPayError) {
      // unmapped errors
      setErrorFields({
        message: t('payments:customPayment.payment.error.messages.default'),
      })
    }
  }, [isConfirmPayError, applepay.error, errorKey])
}

export const useIsOrderAmountExceedsSpendCapEnabled = (): boolean => {
  const cohort = useCohort()
  const isFeatureFlagEnabled = useFlag(Features.ORDER_AMOUNT_EXCEEDS_SPEND_CAP)
  const isCashOnly = cohort === Cohort.CashOnly
  const isOrderAmountExceedsSpendCapEnabled = isFeatureFlagEnabled && !isCashOnly

  return isOrderAmountExceedsSpendCapEnabled
}

export const useIsOrderAmountExceedsSpendCap = (
  errorCode: TerminalErrorCode
): {
  isOrderAmountExceedsSpendCap: boolean
  isOrderAmountExceedsSpendCapAndNotEnabled: boolean
} => {
  const isOrderAmountExceedsSpendCapEnabled = useIsOrderAmountExceedsSpendCapEnabled()
  const isOrderAmountExceedsSpendCapError = errorCode === TerminalErrorCode.OrderAmountExceedsSpendCap
  const isOrderAmountExceedsSpendCap = isOrderAmountExceedsSpendCapEnabled && isOrderAmountExceedsSpendCapError
  const isOrderAmountExceedsSpendCapAndNotEnabled = isOrderAmountExceedsSpendCapError && !isOrderAmountExceedsSpendCapEnabled
  return { isOrderAmountExceedsSpendCap, isOrderAmountExceedsSpendCapAndNotEnabled }
}
